import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import { ShoppingCart, Check } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import AuthModal from '@/components/AuthModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { user } = useAuth();
  
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [currency, setCurrency] = useState('INR');
  const [showAuth, setShowAuth] = useState(false);
  const [currentImage, setCurrentImage] = useState('');

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${id}`);
      setProduct(response.data);
      setSelectedSize(response.data.sizes[0]);
      setSelectedColor(response.data.colors[0]);
      setCurrentImage(response.data.images?.[response.data.colors[0].name] || response.data.image);
    } catch (error) {
      console.error('Failed to fetch product:', error);
      toast.error('Failed to load product');
      navigate('/shop');
    } finally {
      setLoading(false);
    }
  };

  const handleColorSelect = (color) => {
    setSelectedColor(color);
    if (product.images && product.images[color.name]) {
      setCurrentImage(product.images[color.name]);
    }
  };

  const handleAddToCart = () => {
    if (!selectedSize || !selectedColor) {
      toast.error('Please select size and color');
      return;
    }

    const price = currency === 'INR' ? product.price_inr : product.price_usd;
    
    addToCart({
      product_id: product.id,
      size: selectedSize,
      color: selectedColor.name,
      quantity: quantity,
      price: price
    });

    toast.success('Added to cart!');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!product) return null;

  const getPrice = () => {
    return currency === 'INR' ? product.price_inr : product.price_usd;
  };

  const getCurrencySymbol = () => {
    return currency === 'INR' ? '₹' : '$';
  };

  return (
    <div className="min-h-screen relative">
      <div className="stars"></div>
      <Navbar />

      <div className="relative z-10 pt-32 pb-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Product Image */}
            <div className="relative" data-testid="product-image">
              <div className="sticky top-32">
                <div className="relative overflow-hidden rounded-3xl bg-white p-8">
                  <img 
                    src={currentImage} 
                    alt={`${product.name} - ${selectedColor?.name}`}
                    className="w-full h-auto rounded-xl shadow-2xl transition-all duration-500"
                  />
                  <div className="absolute top-6 right-6 bg-gradient-to-r from-purple-500 to-blue-500 text-white px-4 py-2 rounded-full text-sm font-semibold z-20">
                    Limited Edition
                  </div>
                </div>
              </div>
            </div>

            {/* Product Info */}
            <div className="space-y-6" data-testid="product-info">
              <div>
                <h1 className="text-4xl sm:text-5xl font-bold mb-4">{product.name}</h1>
                <p className="text-gray-300 text-lg">{product.description}</p>
              </div>

              {/* Currency Selector */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-400">Currency:</span>
                <select 
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  className="bg-transparent border border-gray-700 rounded-lg px-3 py-2"
                  data-testid="currency-selector"
                >
                  <option value="INR" className="bg-gray-900">INR (₹)</option>
                  <option value="USD" className="bg-gray-900">USD ($)</option>
                </select>
              </div>

              {/* Price */}
              <div className="glass-card">
                <div className="flex justify-between items-center">
                  <div>
                    <div className="text-4xl font-bold">
                      {getCurrencySymbol()}{getPrice()}
                    </div>
                    <div className="text-sm text-gray-400 mt-1">Per piece</div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-green-400">In Stock</div>
                    <div className="text-sm text-gray-400">Ready to ship</div>
                  </div>
                </div>
              </div>

              {/* Size Selection */}
              <div data-testid="size-selection">
                <label className="block text-sm font-semibold mb-3">Select Size</label>
                <div className="flex flex-wrap gap-3">
                  {product.sizes.map((size) => (
                    <button
                      key={size}
                      onClick={() => setSelectedSize(size)}
                      className={`px-6 py-3 rounded-lg border-2 font-semibold transition-all ${
                        selectedSize === size
                          ? 'bg-purple-500 border-purple-500 text-white'
                          : 'border-gray-700 hover:border-gray-500'
                      }`}
                      data-testid={`size-${size}`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>

              {/* Color Selection */}
              <div data-testid="color-selection">
                <label className="block text-sm font-semibold mb-3">Select Color</label>
                <div className="flex flex-wrap gap-3">
                  {product.colors.map((color) => (
                    <button
                      key={color.name}
                      onClick={() => handleColorSelect(color)}
                      className={`relative px-6 py-3 rounded-lg border-2 font-semibold transition-all ${
                        selectedColor?.name === color.name
                          ? 'border-purple-500'
                          : 'border-gray-700 hover:border-gray-500'
                      }`}
                      data-testid={`color-${color.name}`}
                    >
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-6 h-6 rounded-full border-2 border-gray-600"
                          style={{ backgroundColor: color.hex }}
                        ></div>
                        <span>{color.name}</span>
                        {selectedColor?.name === color.name && (
                          <Check className="w-4 h-4 text-purple-400" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Quantity */}
              <div data-testid="quantity-selection">
                <label className="block text-sm font-semibold mb-3">Quantity</label>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-12 h-12 rounded-lg border-2 border-gray-700 hover:border-gray-500 font-bold text-xl"
                    data-testid="decrease-quantity"
                  >
                    -
                  </button>
                  <span className="text-2xl font-bold w-12 text-center" data-testid="quantity-value">{quantity}</span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="w-12 h-12 rounded-lg border-2 border-gray-700 hover:border-gray-500 font-bold text-xl"
                    data-testid="increase-quantity"
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Add to Cart */}
              <div className="flex gap-4">
                <button 
                  onClick={handleAddToCart}
                  className="flex-1 btn-primary flex items-center justify-center gap-2 py-4 text-lg"
                  data-testid="add-to-cart-btn"
                >
                  <ShoppingCart className="w-5 h-5" />
                  Add to Cart
                </button>
                <button 
                  onClick={() => {
                    handleAddToCart();
                    navigate('/cart');
                  }}
                  className="flex-1 btn-secondary py-4 text-lg"
                  data-testid="buy-now-btn"
                >
                  Buy Now
                </button>
              </div>

              {/* Features */}
              <div className="glass-card space-y-3">
                <div className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-400" />
                  <span>100% Premium Cotton</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-400" />
                  <span>Pre-shrunk Fabric</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-400" />
                  <span>High-Quality Print</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-400" />
                  <span>Fast Shipping Available</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}
    </div>
  );
};

export default ProductDetail;
